import { companyDocument, isIndividualCompany } from '@/lib/person-type'
import { getOnboardingStatus } from '@/lib/api/onboarding/onboarding-api'
import { useAuth } from '@/store/hooks'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import OnboardingModal from '../onboarding-modal/onboarding-modal'
import RegistrationBlockModal from '../registration-block-modal/registration-block-modal'

interface RegistrationGuardProps {
    children: React.ReactNode
}

/**
 * Dois portões, nesta ordem:
 *
 * 1. **Cadastro obrigatório** (`RegistrationBlockModal`) — sem documento e endereço
 *    da empresa, o painel não abre. Bloqueante, porque nota fiscal, cobrança e
 *    agendamento dependem desses campos.
 * 2. **Assistente de primeira entrada** (`OnboardingModal`) — só depois do item 1,
 *    só no segmento de saúde (fora intermediação) e só enquanto a conta não o
 *    concluiu. **Não é
 *    bloqueante:** tem "Continuar depois", que salva o estado e reabre no próximo
 *    acesso.
 */
export default function RegistrationGuard({
    children,
}: RegistrationGuardProps) {
    const { user, isAuthenticated } = useAuth()
    const location = useLocation()
    const navigate = useNavigate()
    const [showModal, setShowModal] = useState(false)
    const [missingFields, setMissingFields] = useState<string[]>([])
    const [isChecking, setIsChecking] = useState(true)
    const [showOnboarding, setShowOnboarding] = useState(false)
    // Fechar o assistente vale para a sessão inteira: sem isto, cada troca de rota
    // reabriria o modal que o médico acabou de adiar.
    const [onboardingDismissed, setOnboardingDismissed] = useState(false)

    const isAccountPage = location.pathname === '/account'

    const getMissingFields = (): string[] => {
        const missing: string[] = []


        if (!user?.company) {
            missing.push('Dados da empresa')
            return missing
        }

        const company = user.company

        // O documento exigido depende da natureza da conta: PJ tem CNPJ, PF tem
        // CPF. Antes o CNPJ era exigido incondicionalmente, e como o cadastro de
        // pessoa física nasce sem CNPJ, o PF entrava e ficava preso neste modal em
        // toda rota do painel — sem caminho de saída, porque a aba Empresa também
        // recusava salvar sem CNPJ.
        if (!companyDocument(company)) {
            missing.push(
                isIndividualCompany(company)
                    ? 'CPF do titular'
                    : 'CNPJ da empresa'
            )
        }
        if (!company.street?.trim()) missing.push('Rua')
        if (!company.number?.trim()) missing.push('Número')
        if (!company.neighborhood?.trim()) missing.push('Bairro')
        if (!company.city?.trim()) missing.push('Cidade')
        if (!company.state?.trim()) missing.push('Estado')
        if (!company.zipCode?.trim()) missing.push('CEP')

        return missing
    }

    useEffect(() => {
        if (!isAuthenticated || !user) {
            setIsChecking(false)
            return
        }

        const fields = getMissingFields()

        if (fields.length > 0 && !isAccountPage) {
            if (location.pathname !== '/account') {
                setMissingFields(fields)
                setShowModal(true)
            }
        } else {
            setMissingFields([])
            setShowModal(false)
        }

        setIsChecking(false)
    }, [isAuthenticated, user, location.pathname, isAccountPage])

    useEffect(() => {
        // O assistente só entra na fila depois que o cadastro obrigatório saiu do
        // caminho — dois modais empilhados deixariam o segundo inalcançável.
        if (!isAuthenticated || !user || onboardingDismissed) return
        if (getMissingFields().length > 0) return

        const organization = user.company?.organization
        // Saúde, e não intermediação: a conta que revende serviço de parceiros não
        // tem médico próprio nem preço no item, então o assistente perguntaria por
        // um consultório que não existe. O backend aplica a mesma regra em
        // `/onboarding/status` — este filtro só evita a chamada.
        if (user.company?.segment !== 'HEALTH') return
        if (user.company?.secondarySegment === 'INTERMEDIATION') return
        // O snapshot da organização já vem no login: quando ele diz que terminou,
        // nem chamamos o status.
        if (organization?.onboardingCompletedAt) return

        let active = true
        const check = async () => {
            const res = await getOnboardingStatus()
            if (!active) return
            if (res.success && res.result?.required) setShowOnboarding(true)
        }
        check()

        return () => {
            active = false
        }
    }, [isAuthenticated, user, onboardingDismissed])

    const handleModalComplete = () => {
        setShowModal(false)
    }

    if (isChecking) {
        return null
    }

    return (
        <React.Fragment>
            {children}
            {showModal && !isAccountPage && (
                <RegistrationBlockModal
                    onComplete={handleModalComplete}
                    missingFields={missingFields}
                />
            )}
            {!showModal && showOnboarding && (
                <OnboardingModal
                    onClose={() => {
                        setShowOnboarding(false)
                        setOnboardingDismissed(true)
                    }}
                    onCompleted={() => {
                        setShowOnboarding(false)
                        setOnboardingDismissed(true)
                    }}
                />
            )}
        </React.Fragment>
    )
}
